import React, { useState, useEffect } from 'react';
import './PaymentSettings.css';
import collegeConfig from '../../config/collegeConfig';
import { FaToggleOn, FaToggleOff, FaSave, FaInfoCircle } from 'react-icons/fa';

const PaymentSettings = () => {
  const [settings, setSettings] = useState({
    paymentMode: 'institute',
    paymentType: 'annual_recurring',
    accountValidityYears: 1,
    amount: 0,
    razorpayKeyId: '',
    razorpayKeySecret: '',
    razorpayWebhookSecret: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/payment/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setSettings(prev => ({ ...prev, ...data.settings, razorpayKeySecret: '', razorpayWebhookSecret: '' }));
      }
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const token = localStorage.getItem('token');
      const payload = { ...settings };
      if (!payload.razorpayKeySecret) delete payload.razorpayKeySecret;
      if (!payload.razorpayWebhookSecret) delete payload.razorpayWebhookSecret;

      const response = await fetch(`${collegeConfig.api.baseURL}/payment/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        setMessage({ text: 'Settings saved successfully!', type: 'success' });
        setSettings(prev => ({ ...prev, ...data.settings, razorpayKeySecret: '' }));
      } else {
        setMessage({ text: data.message || 'Failed to save settings', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Connection error. Please try again.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="ps-loading">
        <div className="ps-spinner"></div>
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="ps-container">
      <div className="ps-header">
        <h2 className="ps-title">Payment Settings</h2>
        <p className="ps-subtitle">Configure how students access the e-logbook system</p>
      </div>

      {message.text && (
        <div className={`ps-alert ps-alert-${message.type}`}>{message.text}</div>
      )}

      {/* Payment Mode Toggle */}
      <div className="ps-card">
        <h3 className="ps-card-title">Billing Mode</h3>
        <p className="ps-card-desc">Select who pays for the e-logbook access</p>

        <div className="ps-toggle-row">
          <div
            className={`ps-toggle-option ${settings.paymentMode === 'institute' ? 'active' : ''}`}
            onClick={() => setSettings(p => ({ ...p, paymentMode: 'institute' }))}
          >
            <div className="ps-toggle-icon">🏛️</div>
            <div className="ps-toggle-label">Institute</div>
            <div className="ps-toggle-desc">University/institution pays directly</div>
          </div>

          <div className="ps-toggle-divider">
            <span
              className="ps-toggle-switch"
              onClick={() =>
                setSettings(p => ({
                  ...p,
                  paymentMode: p.paymentMode === 'institute' ? 'student' : 'institute'
                }))
              }
            >
              {settings.paymentMode === 'student' ? (
                <FaToggleOn size={44} style={{ color: 'var(--primary-color)' }} />
              ) : (
                <FaToggleOff size={44} style={{ color: '#ccc' }} />
              )}
            </span>
          </div>

          <div
            className={`ps-toggle-option ${settings.paymentMode === 'student' ? 'active' : ''}`}
            onClick={() => setSettings(p => ({ ...p, paymentMode: 'student' }))}
          >
            <div className="ps-toggle-icon">🎓</div>
            <div className="ps-toggle-label">Student</div>
            <div className="ps-toggle-desc">Students pay individually via Razorpay</div>
          </div>
        </div>
      </div>

      {/* Student mode config */}
      {settings.paymentMode === 'student' && (
        <>
          <div className="ps-card">
            <h3 className="ps-card-title">Subscription Type</h3>
            <p className="ps-card-desc">Choose how students are billed</p>

            <div className="ps-radio-group">
              <label
                className={`ps-radio-card ${settings.paymentType === 'annual_recurring' ? 'active' : ''}`}
              >
                <input
                  type="radio"
                  name="paymentType"
                  value="annual_recurring"
                  checked={settings.paymentType === 'annual_recurring'}
                  onChange={e => setSettings(p => ({ ...p, paymentType: e.target.value }))}
                />
                <div className="ps-radio-content">
                  <div className="ps-radio-title">🔄 Annual Recurring</div>
                  <div className="ps-radio-desc">
                    Account stays active for 1 year. After expiry, student must repay to reactivate.
                  </div>
                </div>
              </label>

              <label
                className={`ps-radio-card ${settings.paymentType === 'one_time' ? 'active' : ''}`}
              >
                <input
                  type="radio"
                  name="paymentType"
                  value="one_time"
                  checked={settings.paymentType === 'one_time'}
                  onChange={e => setSettings(p => ({ ...p, paymentType: e.target.value }))}
                />
                <div className="ps-radio-content">
                  <div className="ps-radio-title">💳 One-time Purchase</div>
                  <div className="ps-radio-desc">
                    Student pays once. Account stays active for a set number of years.
                  </div>
                </div>
              </label>
            </div>

            {settings.paymentType === 'one_time' && (
              <div className="ps-field ps-validity-field">
                <label className="ps-label">Account validity (years)</label>
                <div className="ps-validity-hint">
                  <FaInfoCircle style={{ marginRight: 6, color: 'var(--info-color)' }} />
                  How many years should the account remain active after one-time payment?
                </div>
                <input
                  type="number"
                  className="ps-input ps-input-sm"
                  min={1}
                  max={10}
                  value={settings.accountValidityYears}
                  onChange={e =>
                    setSettings(p => ({ ...p, accountValidityYears: parseInt(e.target.value) || 1 }))
                  }
                />
              </div>
            )}
          </div>

          <div className="ps-card">
            <h3 className="ps-card-title">Pricing</h3>

            <div className="ps-field">
              <label className="ps-label">Amount (₹)</label>
              <input
                type="number"
                className="ps-input"
                min={0}
                value={settings.amount}
                onChange={e => setSettings(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))}
                placeholder="e.g. 2000"
              />
              <span className="ps-field-hint">Amount students will pay in Indian Rupees</span>
            </div>
          </div>

          <div className="ps-card">
            <h3 className="ps-card-title">Razorpay Configuration</h3>
            <p className="ps-card-desc">
              Enter your Razorpay API keys.{' '}
              <span style={{ color: 'var(--primary-color)', fontWeight: 500 }}>
                Get them from your Razorpay Dashboard → Settings → API Keys
              </span>
            </p>

            <div className="ps-field">
              <label className="ps-label">Key ID</label>
              <input
                type="text"
                className="ps-input ps-mono"
                value={settings.razorpayKeyId}
                onChange={e => setSettings(p => ({ ...p, razorpayKeyId: e.target.value.trim() }))}
                placeholder="rzp_live_xxxxxxxxxx"
              />
            </div>

            <div className="ps-field">
              <label className="ps-label">Key Secret</label>
              <input
                type="password"
                className="ps-input ps-mono"
                value={settings.razorpayKeySecret}
                onChange={e => setSettings(p => ({ ...p, razorpayKeySecret: e.target.value.trim() }))}
                placeholder="Leave blank to keep existing secret"
              />
              <span className="ps-field-hint">Leave blank to keep the existing secret unchanged</span>
            </div>
          </div>

          <div className="ps-card">
            <h3 className="ps-card-title">Payment Webhook (recommended)</h3>
            <p className="ps-card-desc">
              Without this, an account is only created if the student's browser stays open long
              enough to confirm payment after they return from their UPI app — which fails often
              on mobile. The webhook creates the account server-to-server the instant Razorpay
              captures the payment, so it works even if the browser tab is killed or reloaded.
            </p>

            <div className="ps-field">
              <label className="ps-label">1. In Razorpay Dashboard → Settings → Webhooks, add:</label>
              <input
                type="text"
                className="ps-input ps-mono"
                readOnly
                value={`${collegeConfig.api.baseURL}/payment/webhook`}
                onClick={e => e.target.select()}
              />
              <span className="ps-field-hint">Active Events: check "payment.captured"</span>
            </div>

            <div className="ps-field">
              <label className="ps-label">2. Paste the Webhook Secret Razorpay shows you</label>
              <input
                type="password"
                className="ps-input ps-mono"
                value={settings.razorpayWebhookSecret}
                onChange={e => setSettings(p => ({ ...p, razorpayWebhookSecret: e.target.value.trim() }))}
                placeholder="Leave blank to keep existing secret"
              />
              <span className="ps-field-hint">Leave blank to keep the existing secret unchanged</span>
            </div>
          </div>
        </>
      )}

      <div className="ps-actions">
        <button className="ps-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <span className="ps-btn-spinner"></span> Saving...
            </>
          ) : (
            <>
              <FaSave style={{ marginRight: 8 }} /> Save Settings
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default PaymentSettings;
